import { useEffect, useState } from 'react'
import { callsAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import type { CallOptions } from '../types'

// One request per login, shared by every component that asks, like
// useKrakenAccess: whether Lock a call is on (LOCK_A_CALL_ENABLED) is the
// server's to say, and it does not change within a session.
let cached: Promise<CallOptions | null> | null = null
let cachedFor: string | null = null

/** Forget the cached answer (tests). */
export const resetLockACall = () => {
  cached = null
  cachedFor = null
}

/**
 * Lock a call's options when the feature is on for this user, null when it is
 * not, undefined while a superuser's answer is still on its way.
 *
 * Only a superuser asks: the server answers 404 while the flag is off and 403
 * to anyone else, so everyone else would only ever get null. Null on any
 * error: the feature stays hidden rather than flash in. Hiding is
 * presentation only -- every /api/calls route enforces the gate.
 */
const useLockACall = (): CallOptions | null | undefined => {
  const token = useAuthStore((state) => state.token)
  const isSuperuser = useAuthStore((state) => Boolean(state.user?.is_superuser))
  const [options, setOptions] = useState<CallOptions | null | undefined>(undefined)
  useEffect(() => {
    let live = true
    if (!token || !isSuperuser) {
      setOptions(null)
      return undefined
    }
    setOptions(undefined)
    if (!cached || cachedFor !== token) {
      cachedFor = token
      // Inside the chain, so any failure (even a synchronous one) means hidden.
      cached = Promise.resolve()
        .then(() => callsAPI.options())
        .catch(() => {
          cached = null
          return null
        })
    }
    cached.then((o) => live && setOptions(o))
    return () => {
      live = false
    }
  }, [token, isSuperuser])
  return options
}

export default useLockACall
